import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '@/services/base.query';
import { LoginRequest, LoginResponse } from '@/services/api.types';

const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  endpoints: builder => ({
    // --- New BE (naestved v1) multistep login — batch 001 (ADR-03) ---
    // POST /v1/login: verify password → oneof completed{token} | pending{tmp_token,next,mfa}.
    // baseQuery carries no auth header — this is the unauthenticated entry point.
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: body => ({
        url: '/v1/login',
        method: 'POST',
        body,
      }),
    }),
    // POST /v1/login/mfa/verify: submit the OTP. Bearer = tmp step token (aud=mfa).
    loginMfaVerify: builder.mutation<
      LoginResponse,
      { code: string; tmpToken: string }
    >({
      query: ({ code, tmpToken }) => ({
        url: '/v1/login/mfa/verify',
        method: 'POST',
        body: { code },
        headers: { Authorization: `Bearer ${tmpToken}` },
      }),
    }),
    // POST /v1/login/mfa: resend a fresh OTP; the client keeps the same tmp token.
    loginMfaResend: builder.mutation<LoginResponse, { tmpToken: string }>({
      query: ({ tmpToken }) => ({
        url: '/v1/login/mfa',
        method: 'POST',
        body: {},
        headers: { Authorization: `Bearer ${tmpToken}` },
      }),
    }),
    // POST /v1/login/password: forced-change (PWD) gate — submit the new password.
    // Bearer = tmp step token (aud=pwd). Returns oneof completed{token} |
    // pending{next} (batch 002, ADR-03). BE endpoint pending; body field name is
    // co-design (contract §1.2(d)); protojson decode tolerates snake/camel.
    loginPassword: builder.mutation<
      LoginResponse,
      { newPassword: string; tmpToken: string }
    >({
      query: ({ newPassword, tmpToken }) => ({
        url: '/v1/login/password',
        method: 'POST',
        body: { new_password: newPassword },
        headers: { Authorization: `Bearer ${tmpToken}` },
      }),
    }),
    // GET /v1/login/legal/url: signed short-lived URL for a legal doc's
    // content; aud=ldoc tmp token is the Bearer. Mutation (not query) — the URL is
    // freshly signed (~30–60s) and must NOT be cached across renders. Batch 003.
    loginLegalDocumentUrl: builder.mutation<
      { url: string },
      { versionId: string; lang: string; tmpToken: string }
    >({
      query: ({ versionId, lang, tmpToken }) => ({
        url: '/v1/login/legal/url',
        method: 'GET',
        params: { version_id: versionId, lang },
        headers: { Authorization: `Bearer ${tmpToken}` },
      }),
    }),
    // POST /v1/login/legal/accept: record acceptance of the pending
    // tos/pp versions (only the ones actually pending — omit the absent one),
    // clearing the LDOC gate; returns LoginResp (next gate or completed). aud=ldoc
    // tmp token is the Bearer. Batch 003; BE endpoint pending.
    loginLegalDocumentAccept: builder.mutation<
      LoginResponse,
      {
        tosVersionId?: string;
        ppVersionId?: string;
        platformVersion?: string;
        tmpToken: string;
      }
    >({
      query: ({ tosVersionId, ppVersionId, platformVersion, tmpToken }) => ({
        url: '/v1/login/legal/accept',
        method: 'POST',
        body: {
          tos_version_id: tosVersionId,
          pp_version_id: ppVersionId,
          platform_version: platformVersion,
        },
        headers: { Authorization: `Bearer ${tmpToken}` },
      }),
    }),
    // POST /v1/sso/legal/accept: LoginLegalDocumentAccept's SSO twin (batch 004,
    // ADR-9). Same request, same response, same BizLegal.Accept behind it — the two
    // differ in their LAST LINE ONLY (advance vs signLean for an SSO session), which
    // is why the contract keeps them adjacent rather than in separate files.
    //
    // Two endpoints rather than one because the discriminator is the AUDIENCE, not a
    // claim: the tmp ticket is aud=ldoc for the password flow and aud=ldoc.sso for
    // SSO, and there is no transport for a claim through the step middleware. This
    // also makes it fail-closed for free — a password ticket here is a 401 and an SSO
    // ticket on /v1/login/legal/accept is a 401 — which is what physically stops the
    // SSO leg reaching `advance`, whose unconditional password-credential lookup
    // would 500 on an SSO-only account.
    ssoLegalDocumentAccept: builder.mutation<
      LoginResponse,
      {
        tosVersionId?: string;
        ppVersionId?: string;
        platformVersion?: string;
        tmpToken: string;
      }
    >({
      query: ({ tosVersionId, ppVersionId, platformVersion, tmpToken }) => ({
        url: '/v1/sso/legal/accept',
        method: 'POST',
        body: {
          tos_version_id: tosVersionId,
          pp_version_id: ppVersionId,
          platform_version: platformVersion,
        },
        headers: { Authorization: `Bearer ${tmpToken}` },
      }),
    }),
    // --- Password recovery — batch 004 (ADR-1 / ADR-2) ---
    // POST /v1/recovery/password/request: ask for a reset email. A MUTATION, not a
    // lazy query — the legacy GET carried a side effect (it sends mail), which
    // ADR-1 refused to keep. Consequence the caller must respect: a mutation
    // trigger resolves to `{data} | {error}` and has NO `isSuccess`, so
    // destructuring one off it yields `undefined` forever.
    //
    // 200 with an empty body is the ONLY status this can return: account absent, no
    // email on file, patient account, and send-limit exhausted are all deliberately
    // indistinguishable (anti-enumeration, contract §1). There is no error branch to
    // map — treat every resolution as "mail may have been sent".
    recoveryPasswordRequest: builder.mutation<void, { username: string }>({
      query: ({ username }) => ({
        url: '/v1/recovery/password/request',
        method: 'POST',
        body: { username },
      }),
    }),
    // POST /v1/recovery/password/reset: spend the emailed token and set the new
    // password (batch 004, ADR-1/ADR-2). `token` is the opaque base64url of 32
    // random bytes from the link — it carries no account name and the FE never
    // decodes it. Status contract: 200 ok · 410 token invalid/used/expired ·
    // 422 password rejected by server policy (token NOT spent, same link can be
    // retried) · 400 only from PGV on an empty field.
    recoveryPasswordReset: builder.mutation<
      void,
      { token: string; newPassword: string }
    >({
      query: ({ token, newPassword }) => ({
        url: '/v1/recovery/password/reset',
        method: 'POST',
        body: { token, new_password: newPassword },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginMfaVerifyMutation,
  useLoginMfaResendMutation,
  useLoginPasswordMutation,
  useLoginLegalDocumentUrlMutation,
  useLoginLegalDocumentAcceptMutation,
  useSsoLegalDocumentAcceptMutation,
  useRecoveryPasswordRequestMutation,
  useRecoveryPasswordResetMutation,
} = authApi;
export default authApi;
